import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import {
	Button,
	Col,
	Modal,
	Row,
	Select,
	Space,
	Tooltip,
	UploadFile,
} from 'antd';
import Title from 'antd/es/typography/Title';
import Upload, { UploadChangeParam } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { getApiExcel } from 'apis';
import Buttons from 'components/Global/Buttons';
import DatePickerField from 'components/Global/BackAndForwardDatePicker';
import moment from 'moment';
import { useState } from 'react';
import { AiFillFileExcel } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { formatToDateOnly, invalidText, toastText } from 'utils/utils';
import styles from './index.module.scss';
import DateRangePickerField from 'components/Global/BackAndFoewardRangePicker';
import { employeeMappingApi } from 'Api/employeeMapping';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { setOcrData } from '../../../redux/slices/OcrTimeLogsSlice';
import dayjs from 'dayjs';

type UploadFormState = {
	date: any;
	branchId: string;
	departmentId: string;
	file: UploadFile<any> | null;
};
interface Props {
	statusFilterValue: string;
	handleStatusFilter: (value: string) => void;
	options: any[];
	handleDatePicker: (value: [string, string]) => void;
	dateValue: [string, string];
	dropdownOptionsLoading: boolean;
	payPeriodOptions: any[];
	payPeriodLoading: boolean;
	currentPayPeriodIndex: number;
	onPayPeriodChange: (index: number, payPeriod: any) => void;
	payPeriodsForNavigation: any;
	supervisorId: any;
	addPermission: any;
}

const TimeLogsTableHeader = ({
	statusFilterValue,
	handleStatusFilter,
	options,
	handleDatePicker,
	dateValue,
	dropdownOptionsLoading,
	payPeriodOptions,
	payPeriodLoading,
	currentPayPeriodIndex,
	onPayPeriodChange,
	payPeriodsForNavigation,
	supervisorId,
	addPermission,
}: Props) => {
	const [fileList, setFileList] = useState<any[]>([]);
	const [isExportLoading, setIsExportLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [uploadState, setUploadState] = useState<UploadFormState>({
		date: dayjs(),
		branchId: '',
		departmentId: '',
		file: null,
	});
	const [uploadError, setUploadError] = useState({
		date: false,
		file: false,
	});
	const { data } = useSelector((state: any) => state.userProfile);
	const dispatch = useDispatch<AppDispatch>();
	let isRemoving = false;
	const navigate = useNavigate();

	const handleExportExcel = async () => {
		try {
			setIsExportLoading(true);
			const startDate = formatToDateOnly(dateValue[0]);
			const endDate = formatToDateOnly(dateValue[1]);
			const supervisorId = statusFilterValue;
			const query = {
				startDate,
				endDate,
				supervisorId,
			};

			const response: any = await getApiExcel(
				'/timeActivities/export',
				query
			);

			const fileName =
				moment(new Date()).format('MM-DD-YYYY_hh-mm-ss') +
				`_timelogs.xlsx`;
			const excelBlob = new Blob([response.data], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			});
			const excelUrl = URL.createObjectURL(excelBlob);
			const link = document.createElement('a');
			link.href = excelUrl;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (err: any) {
			let errorMessage = 'Something went wrong in exporting excel report';

			if (err?.response?.data instanceof ArrayBuffer) {
				try {
					const decoder = new TextDecoder('utf-8');
					const jsonString = decoder.decode(err.response.data);
					const parsedData = JSON.parse(jsonString);

					if (parsedData.message) {
						errorMessage = parsedData.message;
					}
				} catch (parseError) {}
			} else if (err?.response?.data?.message) {
				errorMessage = err.response.data.message;
			} else if (err?.data?.message) {
				errorMessage = err.data.message;
			} else if (err?.message) {
				errorMessage = err.message;
			}

			toastText(errorMessage, 'error');
		} finally {
			setIsExportLoading(false);
		}
	};

	const handleClick = () => {
		setUploadState((prev) => ({
			...prev,
			date: dayjs(),
		}));

		setIsModalOpen(true);
	};

	const uploadButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false,
			disabled: isSubmitLoading,
			onclick: () => {
				handleUploadCancel();
			},
			size: 'small',
			minHeight: '3.5rem',
			minWidth: '12rem',
		},
		{
			text: 'Upload',
			isLoading: isSubmitLoading,
			className: 'btn-blue',
			isSubmit: false,
			disabled: false,
			onclick: () => {
				handleSubmitForLogs();
			},
			size: 'small',
			minHeight: '3.5rem',
			minWidth: '12rem',
		},
	];

	const handleSubmitForLogs = async () => {
		try {
			const { date, file } = uploadState;
			const errors = {
				date: !date,
				// branchId: !branchId,
				file: !file,
			};

			setUploadError({
				date: errors.date,
				file: errors.file,
			});

			if (!date || !file) {
				return;
			}
			setIsSubmitLoading(true);
			let formData = new FormData();
			formData.append('date', formatToDateOnly(uploadState.date));
			formData.append('supervisorId', supervisorId ?? statusFilterValue);
			formData.append('file', fileList[0]);
			const result =
				await employeeMappingApi.uploadAttendanceSheetForSupervisor(
					formData
				);

			dispatch(setOcrData(result));
			handleUploadCancel();

			toastText('Attendance Sheet read successfully', 'success');
			navigate('/time-logs-preview');
		} catch (error: any) {
			let message =
				error?.response?.data?.message ||
				'Something went wrong in uploading attendance Sheet details';
			toastText(message, 'error');
		} finally {
			setIsSubmitLoading(false);
		}
	};

	const handleUploadCancel = () => {
		setUploadState({
			date: null,
			branchId: '',
			departmentId: '',
			file: null,
		});
		setUploadError({
			date: false,
			file: false,
		});
		setFileList([]);
		setIsModalOpen(false);
	};

	const propsUpload = {
		name: 'file',
		maxCount: 1,
		fileList: fileList,
		// 2️⃣ Update beforeUpload validation
		beforeUpload: (file: UploadFile) => {
			const allowedMimes = [
				'application/pdf',
				'image/png',
				'image/jpeg',
				'image/jpg',
			];
			const isAllowed = allowedMimes.includes(file.type || '');
			const isUnder5MB = file && file.size! / 1024 / 1024 < 5;

			if (!isAllowed) {
				toastText(
					'Only PDF, PNG, JPG, or JPEG files are allowed',
					'error'
				);
				return Upload.LIST_IGNORE;
			}

			if (!isUnder5MB) {
				toastText('File must be smaller than 5MB', 'error');
				return Upload.LIST_IGNORE;
			}

			setUploadError((prev) => ({ ...prev, file: false }));
			setUploadState((prev) => ({ ...prev, file }));
			setFileList([file]);
			return false;
		},

		onChange(info: UploadChangeParam<UploadFile>) {
			if (!isRemoving) {
				setUploadState((prev) => ({ ...prev, file: info.file }));
				setFileList([info.file]);

				if (info.file) {
					setUploadError((prev) => ({
						...prev,
						file: false,
					}));
				}
			} else {
				isRemoving = false;
				setUploadState((prev) => ({ ...prev, file: null }));
				setFileList([]);
			}
		},
		onDrop: () => {
			setUploadState((prev) => ({ ...prev, file: null }));
			setFileList([]);
		},
		onRemove: () => {
			isRemoving = true;
		},
	};

	return (
		<div className={styles['employee-header']}>
			<Space>
				{options?.length > 0 && (
					<Select
						className={styles['employee-header-item']}
						value={statusFilterValue}
						options={options}
						onChange={(value) => {
							handleStatusFilter(value);
						}}
						size="large"
						placeholder="Select Status"
						loading={dropdownOptionsLoading}
						disabled={dropdownOptionsLoading}
					/>
				)}
				<DateRangePickerField
					name="payPeriodRange"
					value={dateValue}
					onChange={handleDatePicker}
					showNavigationArrows={true}
					payPeriods={payPeriodOptions.map((period: any) => ({
						id: period.id,
						name: period.name,
						value: period.id,
						label: period.name,
						startDate: period.startDate,
						endDate: period.endDate,
					}))}
					currentPayPeriodIndex={currentPayPeriodIndex}
					payPeriodsForNavigation={payPeriodsForNavigation}
					onPayPeriodChange={onPayPeriodChange}
					allowCustomDateSelection={true}
					disabled={payPeriodLoading}
					required={false}
					isError={false}
					width={'325px'}
				/>
			</Space>
			<Space className="timesheet-buttons">
				{(!invalidText(statusFilterValue) || supervisorId) && (
					<Tooltip
						title={
							supervisorId
								? `Upload time logs for ${
										data?.firstName ?? ' '
								  } ${data?.lastName ?? ' '}`
								: `Upload time logs for ${
										options?.find(
											(sup: any) =>
												sup.value === statusFilterValue
										)?.label || statusFilterValue
								  }`
						}
					>
						{addPermission && (
							<Button
								type="primary"
								icon={<UploadOutlined />}
								onClick={handleClick}
							>
								Upload Time Logs
							</Button>
						)}
					</Tooltip>
				)}

				<Tooltip title="Export to excel">
					<Button
						type="primary"
						loading={isExportLoading}
						icon={<AiFillFileExcel size={20} />}
						onClick={() => {
							handleExportExcel();
						}}
						disabled={isExportLoading}
					/>
				</Tooltip>
			</Space>

			{isModalOpen && (
				<Modal
					open={isModalOpen}
					onCancel={handleUploadCancel}
					footer={null}
					className="document__popup"
					width="600px"
					closable={false}
					maskClosable={false}
				>
					<div className={`${styles['modal']} modal`}>
						<div className={styles['modal-header']}>
							<Title level={4}>
								<span className="modal-title">
									Upload Time Logs
								</span>
							</Title>
						</div>

						<div>
							<Row className={styles['form-container-card']}>
								<Col
									span={24}
									className={`${styles['col']} margin-top-10`}
								>
									<DatePickerField
										label="Date"
										name="date"
										value={uploadState.date}
										required={true}
										helperText="Date is required"
										placeholder="Select Date"
										showNavigationArrows={true}
										navigationStep="day"
										isError={uploadError.date}
										onChange={(value: any) => {
											setUploadState((prevState) => ({
												...prevState,
												date: value,
											}));
											if (value) {
												setUploadError((prev) => ({
													...prev,
													date: false,
												}));
											}
										}}
									/>
								</Col>
							</Row>

							<Row className={styles['form-container-card']}>
								<Col
									span={24}
									className={`${styles['col']} margin-top-10`}
								>
									<div className={styles['document-upload']}>
										<p
											className={
												styles['document-form-label']
											}
										>
											Document{' '}
											<span
												className={styles['required']}
											>
												*{' '}
											</span>
										</p>
										<Dragger
											{...propsUpload}
											className="test"
											accept="image/*,application/pdf"
											style={{
												border: uploadError.file
													? '1px solid #ff4d4f'
													: undefined,
											}}
										>
											<p className="ant-upload-drag-icon">
												<InboxOutlined />
											</p>
											<p className="ant-upload-hint">
												Drag and drop a file or choose
												file from Device Supported file
												formats.
												<br />
												JPG, JPEG, PNG or PDF |{' '}
												<span className="color-purple">
													Maximum file size: 5MB
												</span>
											</p>
										</Dragger>
										{uploadError.file && (
											<div
												style={{
													color: 'red',
													fontSize: '12px',
												}}
											>
												Document is required
											</div>
										)}
									</div>
								</Col>
							</Row>

							<Row className={styles['modal-buttons']}>
								<Buttons buttons={uploadButtons} />
							</Row>
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default TimeLogsTableHeader;
